import {
  Box,
  Typography,
  TextField,
  CardContent,
  Card,
  Stack,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  useTheme,
  Button,
} from "@mui/material";
import React from "react";
import ActivityItem from "./ActivityItem";
import ChangeSummary from "./ChangedSummaryDefaultView";
import { format } from "date-fns";
import {
  ActivityTypeEnum,
  EntityTypeEnum,
} from "./enums/workOrderActivity.enums";
import {
  entityLabelMap,
  WorkOrderActivityProps,
} from "./records/activityPropsRecordsUtility";
import CommentActivityView from "./CommentView";
import AttachmentActivityView from "./AttachmentActivityView";
import { useMemo, useState } from "react";
import UpdateIcon from "@mui/icons-material/Update";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { woDocNumberStyle } from "../styles/dialogContainerStyles";
import { hoverableStyle } from "components/common/global-style/GlobalStyling";
import { timeOnChip } from "components/QueueBoard/models/workOrderStyles";
import { visualIndicationChip } from "./records/activityStyles";
import { getActivityAction } from "./activityUtility";

const WorkOrderHistoryDialog = ({
  activities,
  setCurrentTabIndex,
}: WorkOrderActivityProps) => {
  const theme = useTheme();
  const [selectedSegmentId, setSelectedSegmentId] = useState<string>("all");
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  // Extract unique segments from activities
  const uniqueSegments = useMemo(() => {
    const segmentMap = new Map<string, string>();
    if (!Array.isArray(activities)) return [];

    activities.forEach((act) => {
      const segmentIdStr = act.segmentId?.toString()?.trim();
      const segmentNameStr = act.segmentName?.trim();
      const relatedEntity = act.relatedEntity?.trim();
      // console.log("Skipping segment:", {
      //   segmentId: act.segmentId,
      //   segmentName: act.segmentName,
      //   relatedEntity: act.relatedEntity,
      // });
      if (
        segmentIdStr &&
        segmentNameStr &&
        relatedEntity === "InvoiceSegmentForDropDown" &&
        !segmentMap.has(segmentIdStr)
      ) {
        segmentMap.set(segmentIdStr, segmentNameStr);
      }
    });

    return Array.from(segmentMap, ([segmentId, segmentName]) => ({
      segmentId,
      segmentName,
    }));
  }, [activities]);

  // Handle dropdown change
  const handleSegmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSegmentId(event.target.value);
  };

  // Handle collapse/expand toggle
  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Filter activities based on selected segment
  const filteredActivities = useMemo(() => {
    if (selectedSegmentId === "all") {
      return activities;
    }
    return activities.filter(
      (activity) => activity.segmentId?.toString() === selectedSegmentId,
    );
  }, [activities, selectedSegmentId]);

  return (
    <Box className="wo-dialog-activity-root">
      {/* Header */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography sx={woDocNumberStyle(theme)}>
          Work Order Activity
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Track all changes and updates to your work orders
        </Typography>
      </Box>
      {/* Segment Filter dropdown */}
      <Box mb={2}>
        <TextField
          size="small"
          fullWidth
          select
          label="Filter by Segment"
          value={selectedSegmentId}
          onChange={handleSegmentChange}
        >
          <MenuItem value="all">All</MenuItem>
          {uniqueSegments.map((segment) => (
            <MenuItem key={segment.segmentId} value={segment.segmentId}>
              {segment.segmentName}
            </MenuItem>
          ))}
        </TextField>

        {/* Legend Row */}
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Legend:
          </Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                size="small"
                sx={{ bgcolor: "#3DA554", ...visualIndicationChip }}
              />
              <Typography variant="body2">Created</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                size="small"
                sx={{ bgcolor: "blue", ...visualIndicationChip }}
              />
              <Typography variant="body2">Updated</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                size="small"
                sx={{ bgcolor: "red", ...visualIndicationChip }}
              />
              <Typography variant="body2">Deleted</Typography>
            </Box>
          </Box>
        </Box>

        {/* Collapse/Expand Toggle Button */}
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            onClick={handleToggleCollapse}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 500,
            }}
          >
            {isCollapsed ? "Expand All" : "Collapse All"}
          </Button>
        </Box>
      </Box>
      {/* Main Content */}
      <Box
        className="wo-dialog-activity-scroll"
        sx={{
          mt: "10px",
          pb: "2px",
        }}
      >
        <Stack spacing={2}>
          {filteredActivities.length > 0 ? (
            filteredActivities
              .filter(
                (activity) =>
                  activity.relatedEntity !== "InvoiceSegmentForDropDown",
              )
              .map((history, index) => (
                <Card
                  key={index}
                  variant="outlined"
                  sx={{
                    ...hoverableStyle(theme),
                    mb: 2,
                    borderRadius: "5px",
                    boxShadow: 3,
                    borderLeft: "5px solid #1976d2",
                    backgroundColor:
                      theme.palette.mode === "light" ? "#fff" : "#181F2A",
                  }}
                >
                  <CardContent
                    sx={{
                      padding: "5px",
                      paddingBottom: "5px",
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {format(
                          new Date(history.createdDate),
                          "MMM d, yyyy, h:mmaaa",
                        )}
                      </Typography>
                    </Stack>
                    {/* Need to adjust the text */}
                    {history.relatedEntity === EntityTypeEnum.SegAttachment ||
                    history.relatedEntity ===
                      EntityTypeEnum.SegAttachmentImage ? (
                      <AttachmentActivityView
                        color={
                          history.activityType === ActivityTypeEnum.INSERT
                            ? "green"
                            : history.activityType === ActivityTypeEnum.UPDATE
                              ? "blue"
                              : "red"
                        }
                        user={history.changedBy}
                        action={getActivityAction(history)}
                        target={String(
                          entityLabelMap[history.relatedEntity]?.(history) ||
                            "Unknown Entity",
                        )}
                        entity={history}
                        setCurrentTabIndex={setCurrentTabIndex}
                      />
                    ) : (
                      <ActivityItem
                        color={
                          history.activityType === ActivityTypeEnum.INSERT
                            ? "green"
                            : history.activityType === ActivityTypeEnum.UPDATE
                              ? "blue"
                              : "red"
                        }
                        user={history.changedBy}
                        action={getActivityAction(history)}
                        target={
                          entityLabelMap[history.relatedEntity]?.(history) ||
                          "Unknown Entity"
                        }
                        entity={history}
                        setCurrentTabIndex={setCurrentTabIndex}
                        isCollapsed={isCollapsed}
                        onToggleCollapse={() => {
                          // Individual toggle callback - can be used for future functionality
                        }}
                      />
                    )}
                    {/* Always show comments, but conditionally show other changed fields */}
                    {history.changedFields &&
                      (() => {
                        switch (history.relatedEntity) {
                          case "SegmentComment":
                            return (
                              <CommentActivityView
                                changedFieldsJson={history.changedFields}
                                state={history.activityType}
                              />
                            );
                          default:
                            return (
                              <ChangeSummary
                                changedFieldsJson={history.changedFields}
                                isCollapsed={isCollapsed}
                                onToggleCollapse={() => {
                                  // Individual toggle callback - can be used for future functionality
                                }}
                              />
                            );
                        }
                      })()}
                  </CardContent>
                </Card>
              ))
          ) : (
            <Typography>No activity found for the selected segment.</Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default WorkOrderHistoryDialog;
